/* eslint @typescript-eslint/strict-boolean-expressions: off */

import { useContext, useState } from "react";
import styles from "./AuthPages.module.css";
import { Form } from "src/components/ui-components/Form/Form";
import { InputField } from "src/components/ui-components/Input/InputField";
import { Button } from "src/components/ui-components/Button/Button";
import { useSignupUrl } from "src/hooks/useSignupUrl";
import { AnchorButton } from "src/components/ui-components/Button/AnchorButton";
import { AuthError } from "src/utils/api/auth";
import { CSRFContext } from "src/components/csrf/csrf";
import { firstQueryValue, parseBrowserQuery } from "src/utils/links/query";
import { frontend } from "src/utils/env/env";
import { authErrorLabels } from "./authPageErrors";
import { useLoginInWithEth } from "shared/web3/useLoginWithEth";
import { HTTPError } from "src/utils/http/http";
import { Icon } from "src/components/icon/icon";
import { AnchorButtonTextOnly } from "src/components/ui-components/ButtonTextOnly/AnchorButtonTextOnly";
import { Logo } from "./Logo";

export function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const signUpUrl = useSignupUrl();

  const { csrf } = useContext(CSRFContext);
  // The "next" form field controls where the user is redirected after login.
  // By default we redirect to the Next frontend's personalized home page.
  const query = parseBrowserQuery();
  const next = firstQueryValue(query, "next") || frontend();
  const ref = firstQueryValue(query, "ref");

  const errorParam = firstQueryValue(query, "error");
  const errorKey = errorParam in authErrorLabels ? errorParam : undefined;
  const error = errorKey ? new AuthError(errorKey) : undefined;

  const helperText =
    ref === "registerConfirm"
      ? "Your email was confirmed. Please sign in."
      : ref === "resetConfirm"
      ? "Your password was changed. Please sign in."
      : undefined;

  return (
    <div className={styles.wrapper}>
      <div className={styles.logo}>
        <Logo />
      </div>
      <div className={styles.section}>
        {/* The login form works differently than the rest of the auth forms
      since a successful login results in a browser redirect. */}
        <Form
          size="L"
          className={styles.form}
          method="post"
          action="/api/auth"
          aria-label="Sign in"
        >
          <h1 className={styles.title}>
            Sign in
            {helperText && <p className={styles.helperText}>{helperText}</p>}
          </h1>

          <input type="hidden" name="csrf" value={csrf || ""} />
          <input type="hidden" name="action" value="login" />
          <input type="hidden" name="next" value={next} />

          <InputField
            size="L"
            style="contained"
            label="Username or email"
            name="username"
            autoComplete="username"
            placeholder="Satoshi"
            onChange={(event) => {
              setUsername(event.target.value);
            }}
            value={username}
            required
          />
          <InputField
            size="L"
            name="password"
            type="password"
            minLength={8}
            label={
              <div className={styles.resetPassword}>
                Password
                <AnchorButtonTextOnly
                  href="/auth/reset"
                  theme="primary"
                  size="M"
                >
                  Reset Password
                </AnchorButtonTextOnly>
              </div>
            }
            placeholder="Your password..."
            style="contained"
            onChange={(event) => {
              setPassword(event.target.value);
            }}
            value={password}
            required
          />

          {error && (
            <p role="alert" className={styles.error}>
              {error.label}
            </p>
          )}
          <Button
            size="L"
            theme="primary"
            className={styles.button}
            type="submit"
          >
            Sign in
          </Button>
        </Form>

        <hr />

        <ButtonEthereumLogin />

        <hr />

        <h2 className={styles.subtitle}>Need an account?</h2>

        <AnchorButton
          size="L"
          theme="primary-light"
          href={signUpUrl}
          className={styles.button}
        >
          Sign up
        </AnchorButton>
      </div>
    </div>
  );
}

const ETHEREUM_ERROR_MAPPING: Record<string, Error> = {
  userNotFoundForAddress: new Error(
    "No user linked with this address. Link your address in Settings."
  ),
};

const ButtonEthereumLogin = () => {
  const { login: loginWithEth, error, loading } = useLoginInWithEth();
  const parseError = () => {
    if (error === null) return null;
    if (
      error instanceof HTTPError &&
      ETHEREUM_ERROR_MAPPING[error.errorKey()]
    ) {
      return ETHEREUM_ERROR_MAPPING[error.errorKey()];
    }
    // eslint-disable-next-line no-console
    console.log(error);
    return new Error("Sign in with Ethereum failed. Please Try again.");
  };
  const parsedError = parseError();

  return (
    <>
      <Button
        disabled={loading}
        onClick={loginWithEth}
        className={styles.button}
        theme="primary"
        size="L"
      >
        <Icon icon="ethereum" />{" "}
        {loading ? "Verifying" : "Sign in with Ethereum"}
      </Button>

      {parsedError && (
        <p role="alert" className={styles.error}>
          {parsedError.message}
        </p>
      )}
    </>
  );
};
